// ============================================================================
// Layer Group operational status — the one canonical owner
// ============================================================================
//
// A Layer Group's operational status lives in exactly one place: the normalized
// row in the `ppp-layer-groups-v1` app_store blob, edited on /layer/groups. Its
// two values are `active` and `inactive`.
//
// IT LIVES IN src/lib, NOT src/layer, AND THAT IS A BOUNDARY RATHER THAN A
// PREFERENCE. Its consumers are the Layer views, the two Home dashboards, the
// shared admin daily modal AND two src/webforms presenters, and a webform
// presenter may not import from a program directory — the guards in
// tests/static/authenticated_webformhub_bridge_static.test.js and the two
// projection-retirement files pin exactly that. This is pure domain logic with
// no client, no scope and no React, which is what src/lib is for; it sits
// beside src/lib/layerHousing.ts for the same reason.
//
// ---------------------------------------------------------------------------
// 1. ELIGIBILITY IS THE EXACT STRING `active`. NOTHING ELSE.
// ---------------------------------------------------------------------------
// Not `Active`. Not `ACTIVE`. Not `' active '`. Not `retired`, `inactive`, a
// missing key, or any value nobody has seen. Every one of those is INACTIVE.
//
// This is deliberately STRICTER than a tolerant comparison, and the strictness
// is the point. The only writer of this field is the two-option control on
// /layer/groups, which writes the exact lowercase literals; `retired` is the
// one legacy value in the blob and it is already inactive under this rule. A
// tolerant comparison would silently accept a hand-edited `Active` as
// operational, which means a typo in a JSON document could quietly put a
// retired group back on every daily form. Eligibility is a whitelist of ONE
// string, and a value that is not that string does not get the benefit of the
// doubt.
//
// NAMES are a different question and ARE normalized (case, padding, internal
// whitespace), because a name is matched across three independently maintained
// sources — the blob, `layer_housings.housing_name`, and the persisted
// `batch_label` on a daily report — and those genuinely disagree about spacing.
// Normalizing a name changes WHICH ROW a rule is about; normalizing a status
// would change WHETHER THE RULE FIRES. Only the first is safe.
//
// ---------------------------------------------------------------------------
// 2. DUPLICATES FAIL CLOSED
// ---------------------------------------------------------------------------
// Two rows can carry the same name. If any of them is not exactly `active`,
// the NAME is inactive — a duplicate row is not a second opinion that can
// outvote a deliberate retirement.
//
// ---------------------------------------------------------------------------
// 3. THE CANONICAL READ IS A THREE-STATE CONTRACT, NOT AN ARRAY
// ---------------------------------------------------------------------------
// `layer_batches`, `layer_housings` and the `webform_config` mirror all carry
// their own `status`, and the mirror is republished by the daily maintenance
// job, so after a group is switched off they can all still say `active` for up
// to a day. The canonical row is what vetoes them.
//
// That veto is only trustworthy when the canonical read SUCCEEDED. A pending
// read and a failed read both yield no rows, and an empty index vetoes nothing
// — so treating either as "resolved, and the blob is empty" would hand the
// operator a dropdown built purely from stale mirror data, with the retired
// group in it, at exactly the moment the canonical answer is unavailable. That
// is the failure this module exists to make impossible:
//
//   pending   the read has not answered yet          -> no selectable choices
//   failed    the read answered with an error, OR    -> no selectable choices,
//             threw, OR the row could not be read       operator-visible retry
//   resolved  the read succeeded (a MISSING row is   -> veto by the index
//             resolved-and-empty, not failed)
//
// `selectableLayerGroupNames` enforces that, and it takes a `keep` label that
// survives all three states: a report already filed under a group must remain
// editable under its own label whatever the canonical read is doing, because
// dropping it would silently rewrite that row's group on the next save.
//
// UNKNOWN NAMES ARE ONLY SAFE ONCE RESOLVED. A housing with no canonical group
// row of its own is left alone — but only after a successful read establishes
// that the blob genuinely has no row for it. While pending or failed, "the blob
// has no row for this name" is indistinguishable from "the blob is unavailable",
// so no name is offered at all.
//
// ---------------------------------------------------------------------------
// WHAT THIS MODULE MUST NOT DO
// ---------------------------------------------------------------------------
// It has no opinion about the `layer_batches` pseudo-batch named 'Retirement
// Home'. That row is a historical aggregate whose non-active lifecycle status
// is load-bearing for feed-cost fan-out, and it is fenced BY NAME in several
// places on purpose. Operational state is DERIVED from the canonical Layer
// Group and displayed; it is never written into `layer_batches`.
//
// Historical reports are outside all of this. A report already carrying an
// inactive group's label keeps that label, label -> batch_id resolution is
// deliberately not vetoed, and list filters keep offering every label that has
// ever been filed.
// ============================================================================

export const LAYER_GROUP_STATUS_ACTIVE = 'active';
export const LAYER_GROUP_STATUS_INACTIVE = 'inactive';

export type LayerGroupStatus = typeof LAYER_GROUP_STATUS_ACTIVE | typeof LAYER_GROUP_STATUS_INACTIVE;

/** The two values the shared Layer Group status control offers, in order. */
export const LAYER_GROUP_STATUS_OPTIONS: ReadonlyArray<{value: LayerGroupStatus; label: string}> = Object.freeze([
  Object.freeze({value: LAYER_GROUP_STATUS_ACTIVE, label: 'Active'}),
  Object.freeze({value: LAYER_GROUP_STATUS_INACTIVE, label: 'Inactive'}),
]);

/** A canonical blob row, reduced to the two fields status decisions read. */
export type LayerGroupStatusRow = {name?: string | null; status?: string | null};

/**
 * A group name reduced to its comparison key: case-folded, trimmed, and with
 * runs of internal whitespace collapsed. `Retirement  Home ` and
 * `retirement home` are the same group; an empty or nameless row has no key and
 * therefore makes no claim.
 *
 * Names are normalized because three independent sources spell the same group;
 * statuses are NOT, for the reason in this file's header.
 */
export function normalizeLayerGroupName(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/**
 * Eligibility is the exact string `active`. A differently-cased or padded
 * value is NOT active — see this file's header for why that is deliberate.
 */
export function isActiveLayerGroupStatus(status: unknown): boolean {
  return status === LAYER_GROUP_STATUS_ACTIVE;
}

/** Everything that is not exactly `active` resolves to `inactive`. */
export function normalizeLayerGroupStatus(status: unknown): LayerGroupStatus {
  return isActiveLayerGroupStatus(status) ? LAYER_GROUP_STATUS_ACTIVE : LAYER_GROUP_STATUS_INACTIVE;
}

/** `Active` / `Inactive` — the label for a stored status, legacy values included. */
export function layerGroupStatusLabel(status: unknown): string {
  return isActiveLayerGroupStatus(status) ? 'Active' : 'Inactive';
}

export type LayerGroupStatusIndex = ReadonlyMap<string, LayerGroupStatus>;

const EMPTY_INDEX: LayerGroupStatusIndex = new Map();

/**
 * Name -> status, over the canonical blob. Bare-string legacy entries are
 * skipped: they carry no status and so make no claim either way. Duplicates
 * fail closed — one non-`active` row is enough to make the name inactive.
 */
export function buildLayerGroupStatusIndex(rows: unknown): LayerGroupStatusIndex {
  if (!Array.isArray(rows) || rows.length === 0) return EMPTY_INDEX;
  const index = new Map<string, LayerGroupStatus>();
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const key = normalizeLayerGroupName((row as LayerGroupStatusRow).name);
    if (!key) continue;
    const status = normalizeLayerGroupStatus((row as LayerGroupStatusRow).status);
    // Once inactive, always inactive: a later active duplicate cannot override.
    if (status === LAYER_GROUP_STATUS_INACTIVE || !index.has(key)) index.set(key, status);
  }
  return index;
}

// ---------------------------------------------------------------------------
// The three-state canonical read
// ---------------------------------------------------------------------------

export const LAYER_GROUP_READ_PENDING = 'pending';
export const LAYER_GROUP_READ_RESOLVED = 'resolved';
export const LAYER_GROUP_READ_FAILED = 'failed';

export type LayerGroupReadState =
  | typeof LAYER_GROUP_READ_PENDING
  | typeof LAYER_GROUP_READ_RESOLVED
  | typeof LAYER_GROUP_READ_FAILED;

export type LayerGroupStatusResolution = {
  state: LayerGroupReadState;
  /** Populated ONLY when `state` is `resolved`; empty otherwise, and unusable. */
  index: LayerGroupStatusIndex;
};

export const PENDING_LAYER_GROUP_STATUS: LayerGroupStatusResolution = Object.freeze({
  state: LAYER_GROUP_READ_PENDING,
  index: EMPTY_INDEX,
});
export const FAILED_LAYER_GROUP_STATUS: LayerGroupStatusResolution = Object.freeze({
  state: LAYER_GROUP_READ_FAILED,
  index: EMPTY_INDEX,
});

/** The catalog payload shape every canonical reader returns. */
export type LayerGroupsPayload = {
  layerGroups?: unknown;
  /** `failed` when the read errored or threw; `resolved` otherwise. */
  layerGroupsStatus?: LayerGroupReadState;
};

/**
 * Fold a route resource's `{data, error}` into the three-state contract.
 *
 * `error` is the hook's own transport failure. `layerGroupsStatus` is the
 * payload's OWN verdict, which exists because the canonical readers are
 * deliberately fail-soft: they must not take a whole daily list down when the
 * blob is unreachable, so they report the failure in the payload instead of
 * throwing. Both are checked; either one means failed.
 *
 * A null payload with no error is PENDING, not resolved-and-empty. That is the
 * whole point: an empty index vetoes nothing, so "not answered yet" must never
 * be mistaken for "answered, and nothing is retired".
 */
export function resolveLayerGroupStatus(source: {
  data?: LayerGroupsPayload | null;
  error?: unknown;
}): LayerGroupStatusResolution {
  if (source && source.error) return FAILED_LAYER_GROUP_STATUS;
  const payload = source ? source.data : null;
  if (!payload) return PENDING_LAYER_GROUP_STATUS;
  if (payload.layerGroupsStatus === LAYER_GROUP_READ_FAILED) return FAILED_LAYER_GROUP_STATUS;
  if (payload.layerGroupsStatus === LAYER_GROUP_READ_PENDING) return PENDING_LAYER_GROUP_STATUS;
  return {state: LAYER_GROUP_READ_RESOLVED, index: buildLayerGroupStatusIndex(payload.layerGroups)};
}

export function isLayerGroupStatusResolved(resolution: LayerGroupStatusResolution | null | undefined): boolean {
  return !!resolution && resolution.state === LAYER_GROUP_READ_RESOLVED;
}

export function isLayerGroupStatusFailed(resolution: LayerGroupStatusResolution | null | undefined): boolean {
  return !!resolution && resolution.state === LAYER_GROUP_READ_FAILED;
}

/**
 * True when the canonical blob KNOWS this name and says it is inactive. Only
 * meaningful against a RESOLVED index; callers reach this through
 * `selectableLayerGroupNames`, which refuses to offer anything at all until
 * then.
 */
export function isLayerGroupNameInactive(index: LayerGroupStatusIndex | null | undefined, name: unknown): boolean {
  if (!index || index.size === 0) return false;
  const key = normalizeLayerGroupName(name);
  if (!key) return false;
  return index.get(key) === LAYER_GROUP_STATUS_INACTIVE;
}

/**
 * The canonical rows that are operationally active, in blob order.
 *
 * Order is part of the contract, not a convenience: the /dailys egg form seeds
 * its four persisted default group names from the first four active rows.
 *
 * This reads the BLOB ITSELF, so it is only ever called where the rows in hand
 * are the canonical ones — a caller holding a pending or failed read has no
 * rows to pass and must not substitute a mirror.
 *
 * A non-array argument still throws, exactly as the `(rows || []).filter(...)`
 * this replaces did. Normalizing a malformed blob is the READ owner's job —
 * `layer.eggDailys.groups` already does it — and swallowing it here would hide
 * a broken document behind blank defaults.
 */
export function filterActiveLayerGroups<Row extends LayerGroupStatusRow>(
  rows: readonly Row[] | null | undefined,
): Row[] {
  const index = buildLayerGroupStatusIndex(rows);
  return (rows || []).filter(
    (row) => row && isActiveLayerGroupStatus(row.status) && !isLayerGroupNameInactive(index, row.name),
  );
}

/**
 * The names a NEW-entry control may offer, given the canonical read's state.
 *
 *   pending / failed  ->  nothing, except `keep`
 *   resolved          ->  `names` minus every name the blob has switched off
 *
 * `keep` is the label a record is already saved under. It survives every state
 * because editing a historical report must not silently rewrite the group it
 * was filed under, and because an unavailable canonical read is a reason to
 * offer FEWER choices, never to drop the one the row already has.
 *
 * Order is preserved. Names the resolved blob has never heard of are left
 * alone — that is a housing or batch with no group row, not a retirement — and
 * that allowance is exactly why it is gated on `resolved`: while pending or
 * failed, "no row for this name" is indistinguishable from "no blob".
 */
/**
 * The message a NEW report is refused with when the canonical read has not
 * resolved.
 */
export const LAYER_GROUPS_UNAVAILABLE_MESSAGE =
  'Layer groups are unavailable right now, so this report cannot be saved. Reload and try again.';

/**
 * Refuse a submission whose selected group is no longer offered.
 *
 * REMOVING AN OPTION DOES NOT CLEAR THE SELECT'S STATE. A form opened while a
 * group was still active keeps that string in React state after the option
 * disappears — from a status change, or from the canonical read failing under
 * it — and would persist a report against a group the form no longer offers.
 * The gate on the option list is therefore only half the contract; this is the
 * other half, applied at the write boundary.
 *
 * Returns null when the submission may proceed, or the operator-facing reason.
 * `selected` may contain blanks (an unused egg slot), which are ignored.
 *
 * This is for NEW reports only. Editing a historical report keeps its
 * saved-label exemption and never reaches here.
 */
export function rejectStaleLayerGroupSelections(
  selected: ReadonlyArray<string | null | undefined>,
  allowedNames: readonly string[] | null | undefined,
  resolution: LayerGroupStatusResolution | null | undefined,
): string | null {
  if (!isLayerGroupStatusResolved(resolution)) return LAYER_GROUPS_UNAVAILABLE_MESSAGE;
  const allowed = new Set((allowedNames || []).map((name) => normalizeLayerGroupName(name)));
  const stale: string[] = [];
  for (const value of selected || []) {
    const key = normalizeLayerGroupName(value);
    if (!key) continue;
    if (!allowed.has(key) && !stale.includes(String(value))) stale.push(String(value));
  }
  if (stale.length === 0) return null;
  return (
    `${stale.join(', ')} ${stale.length === 1 ? 'is' : 'are'} no longer an available layer group. ` +
    'Pick a current group before saving.'
  );
}

export function selectableLayerGroupNames(
  names: readonly string[] | null | undefined,
  resolution: LayerGroupStatusResolution | null | undefined,
  keep?: string | null,
): string[] {
  const keepKey = normalizeLayerGroupName(keep);
  const list = names || [];
  if (!isLayerGroupStatusResolved(resolution)) {
    return keepKey ? list.filter((name) => normalizeLayerGroupName(name) === keepKey) : [];
  }
  const index = resolution ? resolution.index : null;
  return list.filter((name) => normalizeLayerGroupName(name) === keepKey || !isLayerGroupNameInactive(index, name));
}
